// ABOUTME: Renders the dummy phone number records of a shipper: a phone number per set of countries.
// ABOUTME: Used on the sending tab so orders without a phone number still ship to countries that require one.

import { Box, IconButton, InputBase } from "@mui/material";
import { useTheme } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import { InputDropdown } from "../../../../theme/dropdowns/InputDropdown";
import Shbutton from "../../../../theme/buttons/Shbutton";
import Text from "../../../../theme/text/Text";

const DummyPhoneNumbers = ({
    dummyNumbers,
    countries,
    onDummyNumberChange,
    onDeleteDummyNumber,
    onAddDummyNumber
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const countryIndices = (codes) => (codes ?? [])
        .map(code => countries.findIndex(country => country.alpha2 === code))
        .filter(index => index !== -1);

    const handleCountryChange = (index, selectedIndices) => {
        onDummyNumberChange("countries", index, selectedIndices.map(selected => countries[selected].alpha2));
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
            }}
        >
            {dummyNumbers.length === 0 && (
                <Text light>{t("no_dummy_phone_numbers")}</Text>
            )}

            {dummyNumbers.map((dummyNumber, index) => (
                <Box
                    key={`dummynumber-${index}`}
                    sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        "@media screen and (max-width: 47.9375em)": {
                            flexDirection: "column",
                            alignItems: "stretch",
                        }
                    }}
                >
                    {/* Phone number */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            background: colors.grey[100],
                            borderRadius: 5,
                            height: 40,
                            flex: 1,
                            pl: 2,
                        }}
                    >
                        <InputBase
                            height={32}
                            type="text"
                            value={dummyNumber.phone ?? ""}
                            onChange={(e) => onDummyNumberChange("phone", index, e.target.value)}
                            placeholder={t("dummy_phone_number")}
                            sx={{
                                flex: 1,
                            }}
                        />
                    </Box>

                    {/* Countries the number is used for */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            background: colors.grey[100],
                            borderRadius: 5,
                            height: 40,
                            flex: 1,
                            pl: 0.5,
                        }}
                    >
                        <InputDropdown
                            key={`dummynumbercountries-${index}`}
                            name="countries"
                            multiple={true}
                            noSelection={t('choose_countries')}
                            displayName="name"
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search_country_placeholder")
                            }}
                            options={countries ?? []}
                            selected={countryIndices(dummyNumber.countries)}
                            onChange={(selectedIndices) => handleCountryChange(index, selectedIndices)}
                            closeOnSelection={false}
                        />
                    </Box>

                    {/* Delete Button */}
                    <IconButton
                        type="button"
                        onClick={() => onDeleteDummyNumber(index)}
                        sx={{
                            color: colors.red[400]
                        }}
                    >
                        <DeleteIcon/>
                    </IconButton>
                </Box>
            ))}

            <Box>
                <Shbutton
                    className={"group-btn"}
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    onClick={onAddDummyNumber}
                    endIcon={<AddIcon/>}
                    variant="contained"
                >
                    {t('add_dummy_phone_number')}
                </Shbutton>
            </Box>
        </Box>
    );
};

export default DummyPhoneNumbers;
